import { Ellipsis, EllipsisVertical } from "lucide-react";
import { MouseEvent, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";
import { v4 } from "uuid";

import { IgIconButton, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import {
  ExperienceListDto,
  GetScriptStatusResponseBodyType,
  OrganizationDto,
  TabEnumFe,
} from "@intelligems/ig-types";
import { Menu, MenuItem } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useDuplicateExperience } from "@src/api/experiences/duplicate-experience";
import { useResetToPausedExperience } from "@src/api/experiences/reset-to-paused-experience";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { useGetHistory } from "@src/api/histories/get-history";
import { processesKeys } from "@src/api/queryKeys";
import { useGetExperienceSchedules } from "@src/api/schedules/get-schedules-by-experience-id";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { UpgradePlanText } from "@src/components/LimitAccess/access-text";
import { useCheckSubscriptionStatus } from "@src/components/LimitAccess/check-subscriptions-hooks";
import { ScopedCreateExperienceWithDisabled } from "@src/components/LimitAccess/ScopedCreateExperienceWithDisabled";
import { ScopedStartExperienceWithDisabled } from "@src/components/LimitAccess/ScopedStartExperienceWithDisabled";
import {
  isRolloutEnabled,
  onRolloutClick,
  renderRolloutButton,
} from "@src/features/experiments/utils/experienceActionUtils";
import { useDialogs } from "@src/hooks/dialog";
import { useIsExperienceMutating } from "@src/hooks/experiences";
import { EXPERIMENT_ROUTES } from "@src/routes/routes";
import { useDispatch, useSelector } from "@src/store";
import { openDrawer } from "@src/store/slices/drawers";
import { setExperienceTabWithoutRedirect } from "@src/store/slices/experiences";
import { getScheduleExperienceInfo } from "@src/store/slices/experiences/selectors/getScheduleExperienceInfo";
import { Experience } from "@src/store/slices/experiences/types";
import { generatePreviewLink } from "@src/utils/generatePreviewLink";
import {
  mpClickAnalyticsButton,
  mpExperienceCreated,
  mpExperienceEditStarted,
  mpExperienceGenericAction,
} from "@src/utils/mixpanel";
import { calculateProductCategories } from "@src/utils/mpUtils";
import { useQueryClient } from "@tanstack/react-query";

import { getConfirmedIntegrationHistoryAction } from "./utils";
import { copyToClipboard } from "@src/utils/copyToClipboard";

export interface MoreMenuExperimentProps {
  scriptStatus: GetScriptStatusResponseBodyType | null;
  organization: OrganizationDto;
  experience: Experience | ExperienceListDto;
  setCurrExperience?: {
    bivarianceHack(arg: any): void;
  }["bivarianceHack"];
  renderLocation: "overview" | "experience";
  tabClicked?: string;
  analyticsDataNotAvailable?: boolean;
  isDataLoading?: number;
}

type MoreMenuContentProps = MoreMenuExperimentProps & {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  duplicateExperience: ReturnType<typeof useDuplicateExperience>;
  resetToPausedExperiment: ReturnType<typeof useResetToPausedExperience>;
};

export const MoreMenuExperiment = (props: MoreMenuExperimentProps) => {
  const { experience, renderLocation } = props;
  const experienceId = experience.id!;

  const [anchorEl, setAnchorEl] = useImmer<null | HTMLElement>(null);
  // Defer the (expensive) menu body until the menu has been opened at least
  // once. In the experiments list this component is mounted per row inside a
  // virtualized AgGrid, so mounting the full hook stack + MUI <Menu> for every
  // row is what made scrolling janky. The trigger button stays lightweight; the
  // content (and its hooks) only mount on first open and then stay mounted so
  // the open/close transitions are preserved.
  const [hasOpened, setHasOpened] = useImmer(false);

  // These mutations drive the trigger's loading spinner, which must keep
  // reflecting pending state after the menu closes, so they live here (not in
  // the lazily-mounted content) and are passed down for the menu items.
  const duplicateExperience = useDuplicateExperience("experiment");
  const resetToPausedExperiment = useResetToPausedExperience(experienceId);
  // The dialog starts the delete with its own useDeleteExperience(), so this
  // menu's isPending would never change. This hook finds that delete instead.
  const isDeleting = useIsExperienceMutating("deleteExperience", experienceId);

  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setHasOpened(true);
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IgTooltip title={"View more options"}>
        <IgIconButton
          size={"small"}
          id="experiment-page-more-button"
          aria-controls="more-positioned-menu"
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          loading={
            duplicateExperience.isPending ||
            resetToPausedExperiment.isPending ||
            isDeleting > 0
          }
        >
          {renderLocation === "experience" ? (
            <EllipsisVertical size={20} />
          ) : (
            <Ellipsis size={20} />
          )}
        </IgIconButton>
      </IgTooltip>
      {hasOpened && (
        <MoreMenuContent
          {...props}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          duplicateExperience={duplicateExperience}
          resetToPausedExperiment={resetToPausedExperiment}
        />
      )}
    </>
  );
};

const MoreMenuContent = ({
  organization,
  experience,
  scriptStatus,
  setCurrExperience,
  renderLocation,
  tabClicked,
  analyticsDataNotAvailable,
  isDataLoading,
  anchorEl,
  open,
  onClose,
  duplicateExperience,
  resetToPausedExperiment,
}: MoreMenuContentProps) => {
  const experienceId = experience.id!;
  const searchParams = new URLSearchParams(window.location.search);

  const dispatch = useDispatch();
  const isAdmin = useIsAdmin();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const downloadPriceCSVEnabled = useConfiguration("enable_price_csv_download");
  const shouldDuplicateProducts = useConfiguration<boolean>(
    "should_duplicate_products"
  )!.value;
  const { openDialog } = useDialogs();
  const queryClient = useQueryClient();

  const canAnalyzeExperiment = useCheckSubscriptionStatus() || isAdmin;
  const experiencesList = useGetExperiencesList({ category: "experiment" });

  const navigate = useNavigate();
  const handleClose = onClose;

  const canCreateShippingTest = useMemo(() => {
    if (experiencesList.isSuccess) {
      return (
        experiencesList.data.experiencesList.filter(
          (exp) => exp.testTypes.hasTestShipping && exp.status !== "ended"
        ).length === 0
      );
    } else return false;
  }, [experiencesList]);

  const editPath = useMemo(() => {
    return `${EXPERIMENT_ROUTES.Experiment}/${experienceId}?action=edit&type=${experience.type}&tab=${
      experience.status === "pending"
        ? TabEnumFe.TEST_GROUPS
        : TabEnumFe.ANALYTICS
    }`;
  }, [experience.status, experience.type, experienceId]);

  const onRolloutClicked = () => {
    handleClose();
    if (setCurrExperience) {
      setCurrExperience(experience);
    }
    onRolloutClick(experience, storeCurrencyConfig, dispatch);
  };

  const onDeleteExperience = () => {
    openDialog("confirmExperienceDeletion", {
      experience,
      category: "experiment",
      redirect: renderLocation === "experience",
    });
    handleClose();
  };

  const onDuplicateExperience = () => {
    queryClient.setQueryData(processesKeys.one(organization.id), {
      processes: [
        {
          id: v4(),
          orgId: organization.id,
          type: "duplicateExperience",
          severity: "info",
          title: `Duplicating ${experience.name}`,
          isLoading: true,
        },
      ],
    });
    duplicateExperience.mutate({
      body: undefined,
      pathParams: { experienceId },
      queryParams: undefined,
    });
  };

  return (
    <Menu
      id="more-positioned-menu"
      aria-labelledby="more-positioned-button"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      sx={{ zIndex: 9999 }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      {renderLocation === "overview" && (
        <Link
          style={{ textDecoration: "none", color: "black" }}
          to={editPath}
          state={{ id: experienceId }}
          onClick={() => {
            mpExperienceGenericAction(experience, "viewed", "more_menu_test");
            mpExperienceEditStarted(experience);
            handleClose();
          }}
        >
          <MenuItem>
            <IgTypography>
              {experience.status === "ended" || experience.isExternal
                ? "View"
                : "Edit"}
            </IgTypography>
          </MenuItem>
        </Link>
      )}
      <ScheduleOption
        experience={experience}
        onClick={handleClose}
        setCurrExperience={setCurrExperience}
      />
      {/* Add Reset Test option */}
      {experienceId !== undefined &&
        (experience.status === "started" || experience.status === "paused") && (
          <MenuItem
            onClick={() => {
              if (setCurrExperience) {
                setCurrExperience(experience);
              }
              openDialog("confirmReset", {
                action: "reset",
                experienceId,
                experience,
              });
              handleClose();
            }}
          >
            <IgTypography>Reset to pending</IgTypography>
          </MenuItem>
        )}
      {experience.startedAtTs &&
      experience.startedAtTs > 0 &&
      canAnalyzeExperiment &&
      renderLocation === "overview" ? (
        <Link
          style={{ textDecoration: "none", color: "black" }}
          onClick={() => {
            mpClickAnalyticsButton(
              experience.name,
              experienceId,
              "MoreMenu analytics button"
            );
          }}
          to={`${EXPERIMENT_ROUTES.Experiment}/${experienceId}?action=edit&type=${experience.type}&tab=${TabEnumFe.ANALYTICS}`}
        >
          <MenuItem>
            <IgTypography>Analyze</IgTypography>
          </MenuItem>
        </Link>
      ) : null}
      {experienceId !== undefined &&
        !experience.isExternal &&
        (!experience.testTypes.hasTestShipping || canCreateShippingTest) && (
          <ScopedCreateExperienceWithDisabled
            experience={experience}
            debugId={"duplicate-experiment-menu-button"}
            customDisabledTooltipMessage={UpgradePlanText}
          >
            <MenuItem
              style={{ textDecoration: "none", color: "black" }}
              onClick={() => {
                if (setCurrExperience) {
                  setCurrExperience(experience);
                }
                if (
                  experience.testTypes.hasTestPricing &&
                  shouldDuplicateProducts
                ) {
                  openDialog("confirmDuplicateProductTestDuplicate", {
                    onConfirm: onDuplicateExperience,
                  });
                } else {
                  onDuplicateExperience();
                }
                mpExperienceCreated(
                  "test",
                  "test_page",
                  calculateProductCategories(experience),
                  true,
                  experience.id
                );
                handleClose();
              }}
              disabled={duplicateExperience.isPending}
            >
              <IgTypography>Duplicate</IgTypography>
            </MenuItem>
          </ScopedCreateExperienceWithDisabled>
        )}
      {experienceId !== undefined && (
        <MenuItem
          onClick={() => {
            if (setCurrExperience) {
              setCurrExperience(experience);
            }
            if (!scriptStatus?.exists) {
              openDialog("redirectToPlugin", {
                id: experienceId,
                experience,
                path: generatePreviewLink({ organization, experience }),
              });
            } else {
              openDialog("previewExperiment", {
                id: experienceId,
                path: generatePreviewLink({ organization, experience }),
              });
            }
            handleClose();
          }}
        >
          <IgTypography>Preview</IgTypography>
        </MenuItem>
      )}
      {experienceId !== undefined &&
      shouldDuplicateProducts &&
      experience.testTypes.hasTestPricing ? (
        <MenuItem
          onClick={() => {
            if (renderLocation === "overview") {
              dispatch(setExperienceTabWithoutRedirect("mods"));
              void navigate(
                `${EXPERIMENT_ROUTES.Experiment}/${experienceId}?action=edit&type=${experience.type}&tab=${TabEnumFe.MODS}`,
                {
                  state: { tab: "mods", showDuplicateProducts: true },
                }
              );
            } else {
              openDialog("experienceProductIssues", {
                showStartTestRow: false,
              });
            }
            handleClose();
          }}
        >
          <IgTypography>View duplicates</IgTypography>
        </MenuItem>
      ) : null}
      {renderRolloutButton(
        tabClicked || "",
        analyticsDataNotAvailable || false,
        isDataLoading || 0,
        experience,
        "Menu"
      ) ? (
        <ScopedStartExperienceWithDisabled
          experience={experience}
          debugId={"rollout-experiment-menu-button"}
          customDisabledTooltipMessage={UpgradePlanText}
        >
          <MenuItem
            disabled={!isRolloutEnabled(experience)}
            onClick={onRolloutClicked}
          >
            Roll out
          </MenuItem>
        </ScopedStartExperienceWithDisabled>
      ) : null}
      {experienceId !== undefined &&
        renderLocation === "overview" &&
        !experience.isExternal &&
        experience.status !== "started" && (
          <MenuItem
            onClick={() => {
              if (setCurrExperience) {
                setCurrExperience(experience);
              }
              onDeleteExperience();
            }}
          >
            <IgTypography>Delete</IgTypography>
          </MenuItem>
        )}
      {experienceId !== undefined &&
        (downloadPriceCSVEnabled?.value || isAdmin) &&
        experience.testTypes.hasTestPricing && (
          <MenuItem
            onClick={() => {
              if (setCurrExperience) {
                setCurrExperience(experience);
              }
              openDialog("downloadPriceCsv");
              handleClose();
            }}
          >
            <IgTypography>Get price CSV</IgTypography>
          </MenuItem>
        )}
      {experienceId !== undefined && (
        <MenuItem
          onClick={() => {
            if (setCurrExperience) {
              setCurrExperience(experience);
            }
            dispatch(
              openDrawer({
                type: "experienceInfo",
                experience,
                isUsedInTest: {
                  name: undefined,
                  id: undefined,
                },
              })
            );
            handleClose();
          }}
        >
          <IgTypography>Show info</IgTypography>
        </MenuItem>
      )}
      {isAdmin &&
        experience.status === "ended" &&
        experience.type !== "shipping" && (
          <MenuItem
            onClick={() => {
              resetToPausedExperiment.mutate({
                body: undefined,
                pathParams: { experienceId },
                queryParams: undefined,
              });
              handleClose();
            }}
          >
            <IgTypography>Reset to paused</IgTypography>
          </MenuItem>
        )}
      {experienceId !== undefined && (
        <MenuItem
          onClick={() => {
            dispatch(
              openDrawer({
                type: "history",
                experienceType: "experiment",
                experienceInfo:
                  renderLocation === "overview" ? experience : undefined,
              })
            );
            handleClose();
          }}
        >
          <IgTypography>View history</IgTypography>
        </MenuItem>
      )}
      {renderLocation === "experience" &&
        searchParams.get("tab") === TabEnumFe.ANALYTICS &&
        experience.status !== "pending" && (
          <MenuItem
            onClick={() => {
              openDialog("dataExport");
              handleClose();
            }}
          >
            <IgTypography>Export data</IgTypography>
          </MenuItem>
        )}
      {experienceId !== undefined && experience.status !== "pending" && (
        <MenuItem
          onClick={() => {
            if (setCurrExperience) {
              setCurrExperience(experience);
            }
            openDialog("shareTestResults", { experienceId });
            handleClose();
          }}
        >
          <IgTypography>Share test results</IgTypography>
        </MenuItem>
      )}
      {experienceId !== undefined && (
        <MenuItem
          onClick={() => {
            const testLink = `${window.location.origin}${EXPERIMENT_ROUTES.Experiment}/${experienceId}`;
            void copyToClipboard(testLink);
            handleClose();
          }}
        >
          <IgTypography>Copy link to test</IgTypography>
        </MenuItem>
      )}
    </Menu>
  );
};

interface ScheduleOptionProps {
  experience: Experience | ExperienceListDto;
  setCurrExperience?: {
    bivarianceHack(arg: any): void;
  }["bivarianceHack"];
  onClick: () => void;
}

const ScheduleOption = ({
  experience,
  setCurrExperience,
  onClick,
}: ScheduleOptionProps) => {
  const experienceId = experience.id!;
  const { openDialog } = useDialogs();

  const schedules = useGetExperienceSchedules(experienceId);
  const history = useGetHistory({
    body: undefined,
    pathParams: { experienceId },
    queryParams: undefined,
  });
  const scheduleInfo = useSelector(
    getScheduleExperienceInfo(experience, history.isSuccess)
  );
  if (experience.status === "ended") return null;

  return (
    <ScopedStartExperienceWithDisabled
      debugId={"schedule-experience-button"}
      experience={experience}
      customDisabledTooltipMessage={UpgradePlanText}
      customEnabledTooltipMessage={scheduleInfo.reason}
    >
      <MenuItem
        disabled={!scheduleInfo.showDialog}
        onClick={() => {
          if (setCurrExperience) {
            setCurrExperience(experience);
          }
          if (
            !getConfirmedIntegrationHistoryAction(history.data?.history || [])
          ) {
            openDialog("confirmIgChecklist", {
              isAdminOverride: false,
              isSchedule: true,
            });
          } else {
            openDialog("scheduleTest", {
              experience,
            });
          }
          onClick();
        }}
      >
        <IgTypography>
          {schedules?.data?.schedules?.length
            ? "Edit or cancel schedule"
            : "Schedule test"}
        </IgTypography>
      </MenuItem>
    </ScopedStartExperienceWithDisabled>
  );
};
